import { useState } from "react";
import { Upload, Select, Spin } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import pdfIcon from "../assets/images/pdf.png";

const { Option } = Select;

interface FileUploadDropdownProps {
  handleFileUpload: (files: File[], documentType: string) => Promise<void>;
  isLoading?: boolean;
}

const FileUploadDropdown: React.FC<FileUploadDropdownProps> = ({
  handleFileUpload,
  isLoading = false,
}) => {
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);

  const handleFileChange = (file: File) => {
    if (!selectedDocument) return false;

    setSelectedFiles((prevFiles) => [...prevFiles, file]);

    let previewUrl = "";

    if (file.type.startsWith("image/")) {
      previewUrl = URL.createObjectURL(file); // Show image preview
    } else if (file.type === "application/pdf") {
      previewUrl = pdfIcon;
    } else {
      previewUrl = "/file-icon.png";
    }

    setFilePreviews((prevPreviews) => [...prevPreviews, previewUrl]);

    return false;
  };

  const handleUpload = async () => {
    if (!selectedFiles.length || !selectedDocument) return;

    try {
      await handleFileUpload(selectedFiles, selectedDocument);

      // Only clear if upload was successful
      if (!isLoading) {
        setSelectedFiles([]);
        setFilePreviews([]);
        setSelectedDocument(null);
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setFilePreviews((prevPreviews) =>
      prevPreviews.filter((_, i) => i !== index),
    );
  };

  return (
    <div className="w-full mb-8 flex flex-col gap-4">
      <div className="flex gap-4">
        <Select
          placeholder="Select document type"
          className="w-full md:w-1/2 lg:w-1/3"
          onChange={setSelectedDocument}
          value={selectedDocument}
          disabled={isLoading}
        >
          {[
            "FARMER_PHOTO",
            "KEBELE_ID",
            "MARRIAGE_CERTIFICATE",
            "LAND_OWNER_CERTIFICATE",
            "TAX_CLERANCE",
            "TIN_DOCUMENT",
            "OTHER"
          ].map((doc) => (
            <Option key={doc} value={doc}>
              {doc}
            </Option>
          ))}
        </Select>

        <Upload
          accept=".pdf,image/*"
          beforeUpload={handleFileChange}
          showUploadList={false}
          multiple
          disabled={!selectedDocument || isLoading}
        >
          <button
            className={`w-full px-4 py-2 rounded-lg border-2 flex items-center justify-center gap-2 ${
              selectedDocument && !isLoading
                ? "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                : "bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed"
            }`}
            disabled={!selectedDocument || isLoading}
          >
            <UploadOutlined /> {isLoading ? "Uploading..." : "Select Files"}
          </button>
        </Upload>
      </div>

      <div className="flex flex-wrap gap-4">
        {selectedFiles.map((file, index) => (
          <div
            key={index}
            className="border p-2 rounded-md text-center w-40 relative"
          >
            {file.type.startsWith("image/") ? (
              <img
                src={filePreviews[index]}
                alt="Preview"
                className="w-full h-32 object-cover rounded-md"
              />
            ) : file.type === "application/pdf" ? (
              <iframe
                src={filePreviews[index]}
                title="PDF Preview"
                className="w-full h-32 border rounded-md"
              />
            ) : (
              <div className="flex flex-col items-center">
                <img
                  src="/file-icon.png"
                  alt="File Icon"
                  className="w-12 h-12 mb-2"
                />
                <span className="text-sm">{file.name}</span>
              </div>
            )}

            <button
              onClick={() => handleRemoveFile(index)}
              className="absolute top-0 right-0 bg-red-500 text-white rounded-full px-2 py-1 text-xs"
              disabled={isLoading}
            >
              ✖
            </button>
          </div>
        ))}
      </div>

      {selectedFiles.length > 0 && (
        <button
          onClick={handleUpload}
          className={`mt-4 px-4 py-2 rounded w-[400px] flex items-center justify-center gap-2
            ${
              isLoading
                ? "bg-green-400 cursor-not-allowed"
                : "bg-green-500 hover:bg-green-600"
            } text-white`}
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Spin />
              <span>Uploading files...</span>
            </div>
          ) : (
            `Upload ${selectedFiles.length} Files`
          )}
        </button>
      )}
    </div>
  );
};

export default FileUploadDropdown;
